import { Box, IconButton, Typography } from "@mui/material";
import moment1 from "moment-timezone";
import React, { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { commonStyles } from "../../../assets/MUIStyles/commonStyles";
import { dashboardStyles } from "../../../assets/MUIStyles/dashboard";
import { ReactComponent as RefreshIcon } from "../../../assets/icons/refresh.svg";
import { getUserAccountsProjectsAction } from "../Actions/CommonActions";
import DynamicTable from "../CommonComponents/Table/DynamicTable";
import { getProjectQueueAction } from "../Projects/Store/projects.action";

function getLongCallsParts(queueRes: Record<string, any> | null) {
  const lc = queueRes?.long_calls || {};
  return {
    thresholdSeconds: lc.threshold_seconds ?? 45,
    windowHours: lc.window_hours ?? 1,
    counts: lc.project_wise_count || {},
    latest: lc.project_wise_latest || {},
  };
}

function getQueuedProjectCounts(queueRes: Record<string, any> | null) {
  return (
    queueRes?.queued_calls?.project_wise_count ||
    queueRes?.project_wise_count ||
    {}
  );
}

function getCompletedProjectCounts(queueRes: Record<string, any> | null) {
  return queueRes?.completed_calls?.project_wise_count || {};
}

function getProjectDetailsMap(queueRes: Record<string, any> | null) {
  return queueRes?.project_details || {};
}

/** Show API instant in the user's local timezone (IANA from browser). */
function formatLongCallToLocal(raw: unknown): string {
  if (raw == null || raw === "") return "";
  const str = String(raw).trim();
  const localTz = moment1.tz.guess();
  const hasExplicitZone = /[zZ]$|[+-]\d{2}:?\d{2}$/.test(str);
  const m = hasExplicitZone
    ? moment1(str).tz(localTz)
    : moment1.utc(str).tz(localTz);
  if (!m.isValid()) return str;
  return m.format("MMM D, YYYY [at] h:mm:ss A");
}

function ProjectQueue() {
  const dispatch = useDispatch();
  const reducer: any = useSelector((state) => state);
  const {
    AccountReducer: { constantData, selectedAccount },
    ProjectReducer: { projectList },
  } = reducer;
  const [data, setData] = useState([]) as any;
  const [loading, setLoading] = useState(false);
  const [refreshLoading, setRefreshLoading] = useState(false);
  const [gateways, setGateways] = useState([]);
  const [longCallsMeta, setLongCallsMeta] = useState({
    thresholdSeconds: 45,
    windowHours: 1,
  });

  const parseRequestReceived = (obj: Record<string, number> | null) => {
    if (!obj) return {};
    const counts: any = {};
    const today = moment1().format("YYYY_MM_DD");

    Object.entries(obj).forEach(([key, value]) => {
      const [projectId, year, month, day] = key.split("_");
      const keyDate = `${year}_${month}_${day}`;

      if (keyDate !== today) return;
      if (!projectId) return;
      counts[projectId] = (counts[projectId] || 0) + (value || 0);
    });
    return counts;
  };

  const mergeAllData = (queueRes, requestsMap) => {
    const projectCounts = getQueuedProjectCounts(queueRes);
    const completedMap = getCompletedProjectCounts(queueRes);
    const detailsById = getProjectDetailsMap(queueRes);
    const { counts: longCounts, latest: longLatest } =
      getLongCallsParts(queueRes);

    const allProjectIds = new Set([
      ...Object.keys(projectCounts),
      ...Object.keys(completedMap),
      ...Object.keys(detailsById),
      ...Object.keys(longCounts),
      ...Object.keys(longLatest),
    ]);

    const merged = Array.from(allProjectIds).map((projectId) => {
      const apiDetail = detailsById[projectId] || {};
      const projectFromList =
        projectList?.find((p) => p?._id === projectId) || {};
      const projectName =
        apiDetail.projectName || projectFromList?.projectName || "-";
      const gateway = apiDetail.gateway ?? "";
      const gatewayName = apiDetail.gatewayName ?? "";
      const longCallLatestRaw = longLatest[projectId];
      const longCallLatestDisplay = longCallLatestRaw
        ? formatLongCallToLocal(longCallLatestRaw)
        : "";

      return {
        projectId,
        projectName,
        count: projectCounts[projectId] ?? 0,
        callCount: completedMap[projectId] ?? 0,
        gateway,
        gatewayName,
        requestsReceived: requestsMap?.[projectId] ?? 0,
        longCallCount: longCounts[projectId] ?? 0,
        longCallLatestDisplay,
      };
    });
    const filtered = merged.filter(
      (item) =>
        item.count !== 0 ||
        item.callCount !== 0 ||
        item.requestsReceived !== 0 ||
        item.longCallCount !== 0
    );

    filtered.sort((a, b) => b.count - a.count);
    return filtered;
  };

  const updateQueueData = (queueRes, previousData) => {
    const projectCounts = getQueuedProjectCounts(queueRes);
    const completedMap = getCompletedProjectCounts(queueRes);
    const detailsById = getProjectDetailsMap(queueRes);
    const { counts: longCounts, latest: longLatest } =
      getLongCallsParts(queueRes);
    const projectIds = new Set([
      ...Object.keys(projectCounts),
      ...Object.keys(completedMap),
      ...Object.keys(detailsById),
      ...Object.keys(longCounts),
      ...Object.keys(longLatest),
    ]);
    const prevById = Object.fromEntries(
      (previousData || []).map((item) => [item.projectId, item])
    );

    const updatedData = Array.from(projectIds).map((projectId) => {
      const existing = prevById[projectId];
      const apiDetail = detailsById[projectId] || {};
      const projectFromList =
        projectList?.find((p) => p?._id === projectId) || {};
      const projectName =
        apiDetail.projectName ||
        existing?.projectName ||
        projectFromList?.projectName ||
        "-";
      const gateway = apiDetail.gateway ?? existing?.gateway ?? "";
      const gatewayName =
        apiDetail.gatewayName ?? existing?.gatewayName ?? "";
      const longCallLatestRaw = longLatest[projectId];
      const longCallLatestDisplay = longCallLatestRaw
        ? formatLongCallToLocal(longCallLatestRaw)
        : "";

      return {
        projectId,
        projectName,
        count: projectCounts[projectId] ?? 0,
        callCount: completedMap[projectId] ?? 0,
        gateway,
        gatewayName,
        requestsReceived: existing?.requestsReceived ?? 0,
        longCallCount: longCounts[projectId] ?? 0,
        longCallLatestDisplay,
      };
    });

    return updatedData
      .filter(
        (item) =>
          item.count !== 0 ||
          item.callCount !== 0 ||
          item.requestsReceived !== 0 ||
          item.longCallCount !== 0
      )
      .sort((a, b) => b.count - a.count);
  };

  const fetchInitialData = async (spin = false) => {
    if (spin) setLoading(true);

    const [queueRes, requestReceivedRes]: any = await Promise.all([
      dispatch(getProjectQueueAction()),
      dispatch(getUserAccountsProjectsAction(selectedAccount?.accountUrl)),
    ]);

    const parsedRequests = parseRequestReceived(requestReceivedRes || {});

    const lc = getLongCallsParts(queueRes || {});
    setLongCallsMeta({
      thresholdSeconds: lc.thresholdSeconds,
      windowHours: lc.windowHours,
    });
    setData(() => mergeAllData(queueRes || {}, parsedRequests));
    setLoading(false);
  };

  const fetchQueueData = async () => {
    const queueRes: any = await dispatch(getProjectQueueAction());
    const lc = getLongCallsParts(queueRes || {});
    setLongCallsMeta({
      thresholdSeconds: lc.thresholdSeconds,
      windowHours: lc.windowHours,
    });
    setData((prev) => updateQueueData(queueRes || {}, prev || []));
  };

  const fetchRequestsData = async () => {
    const requestReceivedRes: any = await dispatch(
      getUserAccountsProjectsAction(selectedAccount?.accountUrl)
    );
    const parsedRequests = parseRequestReceived(requestReceivedRes || {});
    setData((prev: any[]) =>
      (prev || []).map((row) => ({
        ...row,
        requestsReceived: parsedRequests[row.projectId] ?? 0,
      }))
    );
  };

  useEffect(() => {
    setGateways(constantData?.gateways);

    if (selectedAccount?.accountUrl || projectList?.length > 0)
      fetchInitialData(true);

    const queueInterval = setInterval(() => fetchQueueData(), 15000);
    const requestsInterval = setInterval(() => fetchRequestsData(), 10000);

    return () => {
      clearInterval(queueInterval);
      clearInterval(requestsInterval);
    };
  }, [projectList, selectedAccount?.accountUrl]);

  const refreshData = async () => {
    setRefreshLoading(true);
    await fetchInitialData();
    setRefreshLoading(false);
  };

  return (
    <>
      <Box className="flex-between-only">
        <Typography className="font-20-grey my-2">
          Today's Project wise data
        </Typography>
        <Box className="flex-between-only gap-1" sx={{ height: 36 }}>
          <IconButton
            title="Refresh"
            onClick={refreshData}
            sx={{
              ...commonStyles.refreshButtonStyle,
              ...dashboardStyles.refreshAnimation(refreshLoading),
            }}
            disabled={refreshLoading}
          >
            <RefreshIcon className="refresh-icon" height={14} width={14} />
          </IconButton>
        </Box>
      </Box>

      <DynamicTable
        columns={[
          {
            label: "Project Name",
            field: "projectName",
          },
          {
            label: "Project Id",
            field: "projectId",
          },
          {
            label: "Gateway Selected",
            render: (row) => {
              if (row?.gatewayName || row?.gateway) {
                return row.gatewayName
                  ? `${row.gatewayName}${row.gateway ? ` / ${row.gateway}` : ""}`
                  : row.gateway || "-";
              }
              const gateway: any = gateways?.find(
                ({ value }) => value + "_ultra_prod" === row?.gateway
              );
              return gateway?.name
                ? `${gateway?.name || ""} / ${row?.gateway || ""}`
                : "-";
            },
          },
          {
            label: "Requests Received",
            render: (row) => (
              <div style={{ background: "#60A5FA", padding: "8px 8px" }}>
                {row?.requestsReceived ?? 0}
              </div>
            ),
          },
          {
            label: "Calls in Queue",
            render: (row) => (
              <div style={{ background: "#F59E0B", padding: "8px 8px" }}>
                {row?.count}
              </div>
            ),
          },
          {
            label: "Calls Completed",
            render: (row) => (
              <div
                style={{
                  background: "#22C55E",
                  padding: "8px 8px",
                }}
              >
                {row?.callCount}
              </div>
            ),
          },
          {
            label: `Long calls (>${longCallsMeta.thresholdSeconds}s, ${longCallsMeta.windowHours}h window)`,
            render: (row) => (
              <div
                style={{
                  background: "#0D9488",
                  color: "#fff",
                  padding: "8px 8px",
                }}
              >
                {row?.longCallCount ?? 0}
              </div>
            ),
          },
          {
            label: `Last long call (>${longCallsMeta.thresholdSeconds}s)`,
            render: (row) => (
              <Typography variant="body2" sx={{ fontSize: 13 }}>
                {row?.longCallLatestDisplay || "—"}
              </Typography>
            ),
          },
        ]}
        data={data}
        loading={loading}
        rowCount={data?.length}
      />
    </>
  );
}

export default ProjectQueue;
